import { useCallback, useEffect, useMemo, useState } from "react";
import { PageDS, Spacer, TabMenu, ToastContainerDS } from "@qivia/ui";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { AdvancedAnalyticsVehiclesTab } from "./advancedAnalyticsVehiclesTab";
import { HistoryTab } from "./historyTab";
import { MonthlyReportsTab } from "./monthlyReportsTab";
import { VehicleSessionsTab } from "./vehicleSessionsTab";
import {
  advancedAnalyticVehiclesByDepartmentAsync,
  monthlyMileageGapReportsListByDepartmentUserAsync,
  selectAdvancedAnalyticsVehiclesListStatus,
  selectMonthlyReportsListStatus,
} from "../../home/fleet/fleetSlice";
import { selectCurrentDepartment } from "../departments/departmentsSlice";
import { selectManager } from "../../home/homeSlice";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";

export const DepartmentFleet = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const firstPath = location.pathname.split("/")[1];
  const dispatch = useAppDispatch();
  const department = useAppSelector(selectCurrentDepartment);
  const advancedAnalyticVehiclesListStatus = useAppSelector(
    selectAdvancedAnalyticsVehiclesListStatus,
  );
  const monthlyReportsListStatus = useAppSelector(
    selectMonthlyReportsListStatus,
  );
  const manager = useAppSelector(selectManager);
  const params = useParams();
  const [selectedTab, setSelectedTab] = useState<string>(
    params.tab ??
      (department?.hasAdvancedAnalytics
        ? "advanced_analytics"
        : "vehicle_sessions"),
  );

  const isNotProductionEnvironment = useCallback(() => {
    return import.meta.env["VITE_TARGET_ENV"] !== "production";
  }, []);

  const tabs = useMemo(() => {
    const base = [
      ...(department?.hasAdvancedAnalytics
        ? [
            {
              name: "advanced_analytics",
              title: t("fleet.tab.advanced_analytics"),
            },
          ]
        : []),
      ...(isNotProductionEnvironment()
        ? [
            {
              name: "vehicle_sessions",
              title: t("fleet.tab.vehicle_sessions"),
            },
          ]
        : []),
      ...(department?.hasAdvancedAnalytics
        ? [
            {
              name: "history",
              title: t("fleet.tab.tripsHistory"),
            },
            {
              name: "monthly_reports",
              title: t("fleet.tab.monthly_reports"),
            },
          ]
        : []),
    ];
    return base;
  }, [t, isNotProductionEnvironment, department?.hasAdvancedAnalytics]);

  useEffect(() => {
    if (!isNotProductionEnvironment() && selectedTab === "vehicle_sessions") {
      setSelectedTab("advanced_analytics");
    }
  }, [isNotProductionEnvironment, selectedTab]);

  useEffect(() => {
    if (selectedTab && params.tab !== selectedTab) {
      navigate(
        `/${firstPath}/fleet/${selectedTab}${
          params.id !== undefined ? `/${params.id}` : ""
        }`,
      );
    }
  }, [firstPath, navigate, params.id, params.tab, selectedTab]);

  useEffect(() => {
    if (department && advancedAnalyticVehiclesListStatus === "idle") {
      void dispatch(advancedAnalyticVehiclesByDepartmentAsync(department.uuid));
    }
  }, [dispatch, department, advancedAnalyticVehiclesListStatus]);

  useEffect(() => {
    if (department && manager && monthlyReportsListStatus === "idle") {
      void dispatch(
        monthlyMileageGapReportsListByDepartmentUserAsync({
          managerUuid: manager.uuid,
          departmentUuid: department.uuid,
        }),
      );
    }
  }, [department, dispatch, manager, monthlyReportsListStatus]);

  return (
    <PageDS
      title={t(`fleet.title`)}
      toaster={<ToastContainerDS />}
      topElement={
        <>
          <TabMenu
            tabs={tabs}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
          />
          <Spacer y={0.5} />
        </>
      }
      supportButton={{
        showMail: true,
      }}
    >
      <AdvancedAnalyticsVehiclesTab />
      <VehicleSessionsTab />
      <HistoryTab />
      <MonthlyReportsTab />
    </PageDS>
  );
};
